"use client"

/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Auth-aware CTA button for the landing page — switches between
 *            guest and authenticated behavior based on useAuth().
 *   PROPS:   locale, guestHref, guestLabel, variant?, size?, withArrow?, className?
 *   STATE:   useAuth() → { user }
 *   DEPENDS: M-UI-BUTTON, useAuth, next/link, next-intl, phosphor ArrowRight
 *   EMITS:   none (navigation only)
 *   ACCESSIBILITY: focusable link+button, min 44px touch target
 *   LINKS:   M-FEATURE-LANDING-CTA
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-07-05] CLAWE-159: Initial implementation — auth-aware CTA for landing page
 */

import Link from "next/link"
import { useTranslations } from "next-intl"
import { ArrowRight } from "@phosphor-icons/react/dist/ssr"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/use-auth"

interface LandingCtaProps {
  locale: string
  guestHref: string
  guestLabel: string
  variant?: "primary" | "secondary" | "ghost"
  size?: "sm" | "md" | "lg"
  withArrow?: boolean
  className?: string
}

export function LandingCta({
  locale,
  guestHref,
  guestLabel,
  variant = "primary",
  size = "md",
  withArrow = false,
  className,
}: LandingCtaProps) {
  const { user } = useAuth()
  const t = useTranslations("nav")

  const href = user ? `/${locale}/dashboard` : guestHref
  const label = user ? t("dashboardCta") : guestLabel

  return (
    <Link href={href} className={className}>
      <Button variant={variant} size={size}>
        {label}
        {withArrow && <ArrowRight weight="regular" aria-hidden="true" />}
      </Button>
    </Link>
  )
}
